import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { BaseWidget, Dialog, RaisedButton, useWidget } from '@eduact/ed-components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import InvoiceCard from '@Components/Cards/InvoiceCard'
import { dashboardAPI } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { MainTableRow, TableCell } from '@Features/Dashboard/Activity/Components/WalletWidget/WalletWidgetElement'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { manualVcApi, manualVcBase, ManualVcRequest } from './ManualVc.api'
import { openManualWallet } from './ManualVcDraft'

export default function ManualVc() {
	return <BaseWidget widget={ManualVcContent} widgetProps={{ preview: false }} bg="light" />
}
export function ManualVcWidget() {
	return <BaseWidget widget={ManualVcContent} widgetProps={{ preview: true }} bg="light" height="22.95rem" />
}
export function ManualVcContent({ preview = false }: { preview?: boolean }) {
	const { t, i18n } = useTranslation('wallet'),
		{ setTitle, setAction } = useWidget(),
		dispatch = useDispatch()
	const text = (key: string) => t(`manual_vc.${key}`)
	const [params, setParams] = useSearchParams(),
		reference = params.get('request') || ''
	const [page, setPage] = useState(1),
		[lastPage, setLastPage] = useState(1),
		[loaded, setLoaded] = useState(false)
	const [rows, setRows] = useState<ManualVcRequest[]>([]),
		[detail, setDetail] = useState<ManualVcRequest>()
	const [error, setError] = useState(''),
		[proof, setProof] = useState(''),
		[proofError, setProofError] = useState(false),
		[proofLoading, setProofLoading] = useState(false),
		[invoiceOpen, setInvoiceOpen] = useState(false)
	const enabled = useAppSelector(s => s.settings.paymentMethods.manual_vc_enabled)
	const seenApproved = useRef(new Set<string>()),
		version = useRef(0),
		pending = useRef(false)
	const refresh = useCallback(async () => {
		const current = ++version.current
		try {
			const [list, selected] = await Promise.all([
				manualVcApi.list(preview ? 1 : page, preview ? 5 : 20),
				reference ? manualVcApi.detail(reference) : Promise.resolve(undefined),
			])
			if (current !== version.current) return
			setRows(list.data)
			setLastPage(Number(list.meta.last_page))
			setDetail(selected?.status === 'draft' ? undefined : selected)
			setLoaded(true)
			setError('')
			pending.current = list.data.some((r: ManualVcRequest) => r.status === 'pending') || selected?.status === 'pending'
			let approved = false
			for (const row of [...list.data, ...(selected ? [selected] : [])]) {
				if (row.status === 'approved' && !seenApproved.current.has(row.reference)) {
					seenApproved.current.add(row.reference)
					approved = true
				}
			}
			if (approved) dispatch(dashboardAPI.util.invalidateTags(['Profile', 'Invoices']))
		} catch (e: any) {
			if (current === version.current) {
				setError(e?.response?.data?.message || t('manual_vc.error'))
				setLoaded(true)
			}
		}
	}, [page, preview, reference, dispatch, t])
	useEffect(() => {
		refresh()
		const focus = () => {
			if (document.visibilityState === 'visible') refresh()
		}
		const timer = setInterval(() => {
			if (pending.current) focus()
		}, 30000)
		window.addEventListener('focus', focus)
		document.addEventListener('visibilitychange', focus)
		return () => {
			version.current++
			clearInterval(timer)
			window.removeEventListener('focus', focus)
			document.removeEventListener('visibilitychange', focus)
		}
	}, [refresh])
	useEffect(() => {
		setTitle(text('history'))
		setAction(() => <Link to={preview ? manualVcBase : '/dashboard/activity'}>{text(preview ? 'view_all' : 'back_activity')}</Link>)
	}, [preview, t]) // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		let alive = true,
			url = ''
		setProof('')
		setProofLoading(Boolean(detail?.has_proof))
		setProofError(false)
		setInvoiceOpen(false)
		if (detail?.has_proof)
			manualVcApi
				.proof(detail.reference)
				.then(blob => {
					if (alive) {
						url = URL.createObjectURL(blob)
						setProof(url)
					}
				})
				.catch(() => {
					if (alive) setProofError(true)
				})
		return () => {
			alive = false
			if (url) URL.revokeObjectURL(url)
		}
	}, [detail?.reference, detail?.has_proof])
	const close = () => {
		const next = new URLSearchParams(params)
		next.delete('request')
		next.delete('submitted')
		setParams(next)
		setDetail(undefined)
	}
	const date = (value?: string) => (value ? new Date(value).toLocaleString(i18n.language) : '—')
	return (
		<>
			<History dir={i18n.dir()}>
				{error && <p role="alert">{error}</p>}
				{!loaded && <p role="status">{text('loading')}</p>}
				{loaded && !rows.length && <p>{text('empty')}</p>}
				{!!rows.length && (
					<table>
						<thead>
							<tr>
								{['reference', 'credit', 'total', 'submitted', 'status'].map(key => (
									<th key={key}>{text(key)}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map(row => (
								<MainTableRow key={row.reference}>
									<TableCell>
										<Link to={`?request=${row.reference}`} title={row.reference}>
											{preview ? row.reference.slice(0, 8) : row.reference}
										</Link>
									</TableCell>
									<TableCell>{row.amount} VC</TableCell>
									<TableCell>
										{row.total} {text('egp')}
									</TableCell>
									<TableCell>{date(row.submitted_at)}</TableCell>
									<TableCell>
										<StatusChip $bg={row.status === 'approved' ? '#318457' : row.status === 'rejected' ? '#b74545' : '#987323'}>
											{text(row.status)}
										</StatusChip>
									</TableCell>
								</MainTableRow>
							))}
						</tbody>
					</table>
				)}
				{!preview && (
					<Actions>
						<PageArrow
							type="button"
							aria-label={text('previous')}
							title={text('previous')}
							disabled={page <= 1}
							onClick={() => setPage(p => p - 1)}
						>
							{i18n.dir() === 'rtl' ? <MdChevronRight aria-hidden="true" /> : <MdChevronLeft aria-hidden="true" />}
						</PageArrow>
						<span>
							{page} / {lastPage}
						</span>
						<PageArrow
							type="button"
							aria-label={text('next')}
							title={text('next')}
							disabled={page >= lastPage}
							onClick={() => setPage(p => p + 1)}
						>
							{i18n.dir() === 'rtl' ? <MdChevronLeft aria-hidden="true" /> : <MdChevronRight aria-hidden="true" />}
						</PageArrow>
					</Actions>
				)}
			</History>
			<Dialog open={Boolean(detail)} onClose={close} maxWidth="90vw" width="42rem" overflow="auto">
				{detail && (
					<Details dir={i18n.dir()}>
						<h2>{text('details')}</h2>
						<p>
							{text('reference')}: {detail.reference}
						</p>
						<strong>{text(detail.status)}</strong>
						{params.get('submitted') && <p role="status">{text('confirmation')}</p>}
						<p>
							{text('credit')}: {detail.amount} VC
						</p>
						<p>
							{text('fee')}: {detail.fee} {text('egp')}
						</p>
						<p>
							{text('total')}: {detail.total} {text('egp')}
						</p>
						<p>
							{text('receiver')}: <bdi>{detail.receiver_number}</bdi>
						</p>
						<p>
							{text('phone')}: <bdi>{detail.sending_number}</bdi>
						</p>
						<p>
							{text('submitted')}: {date(detail.submitted_at)}
						</p>
						{detail.status === 'pending' && !params.get('submitted') && <p>{text('confirmation')}</p>}
						{detail.status === 'approved' && (
							<>
								<p>
									{text('approved_at')}: {date(detail.reviewed_at)}
								</p>
								<RaisedButton onClick={() => setInvoiceOpen(!invoiceOpen)}>{text('view_invoice')}</RaisedButton>
								{invoiceOpen && detail.invoice && (
									<InvoiceCard
										invoiceReferenceNumber={detail.invoice.invoice_ref}
										date={detail.invoice.created_at}
										amount={String(detail.invoice.total_price)}
										type="recharge"
										provider="manual_vc"
										status="paid"
										providerReference=""
									/>
								)}
							</>
						)}
						{detail.status === 'rejected' && (
							<>
								<p>
									{text('reason')}: {detail.rejection_note}
								</p>
								{enabled && (
									<RaisedButton
										onClick={() => {
											const amount = detail.amount
											close()
											openManualWallet(amount)
										}}
									>
										{text('correct')}
									</RaisedButton>
								)}
							</>
						)}
						{proofLoading && !proofError && <p role="status">{text('loading_image')}</p>}
						{proofError && <p role="alert">{text('proof_error')}</p>}
						{proof && (
							<a href={proof} target="_blank" rel="noreferrer">
								<img
									key={proof}
									src={proof}
									alt={text('screenshot')}
									onLoad={() => setProofLoading(false)}
									onError={() => setProofError(true)}
								/>
							</a>
						)}
						<RaisedButton onClick={close}>{text('close')}</RaisedButton>
					</Details>
				)}
			</Dialog>
		</>
	)
}

export function LegacyManualVc() {
	const location = useLocation()
	const reference = location.pathname.replace('/dashboard/manual-vc', '').split('/').filter(Boolean)[0]
	useEffect(() => {
		if (!reference) return
		if (reference === 'new') openManualWallet()
		else
			manualVcApi
				.detail(reference)
				.then(row => {
					if (row.status === 'draft') openManualWallet(row.amount, row.reference)
				})
				.catch(() => {})
	}, [reference])
	return <Navigate replace to={`${manualVcBase}${reference && reference !== 'new' ? `?request=${reference}` : ''}`} />
}

const History = styled.div`
	overflow: auto;
	border-top: 1px solid #d3d3d3;
	margin-top: 1rem;
	max-height: 100%;
	table {
		width: 100%;
		border-collapse: collapse;
	}
	th {
		font-size: 0.75rem;
		color: #777;
		font-weight: normal;
		padding: 12px 6px;
	}
	td {
		padding: 8px 6px;
	}
	a {
		color: inherit;
		overflow-wrap: anywhere;
	}
`
const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 16px;
`
const Details = styled.div`
	padding: 24px;
	color: ${({ theme }) => theme.colors.independence};
	background: ${({ theme }) => theme.colors.light};
	display: grid;
	gap: 12px;
	overflow-wrap: anywhere;
	p {
		margin: 0;
		line-height: 1.6;
		white-space: pre-wrap;
	}
	img {
		display: block;
		max-width: 100%;
		max-height: 350px;
		object-fit: contain;
	}
`

const StatusChip = styled.span<{ $bg: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: ${({ $bg }) => $bg};
	color: #fff;
	width: auto;
	height: auto;
	min-height: 26px;
	padding: 4px 10px;
	box-sizing: border-box;
	border-radius: 20px;
	white-space: nowrap;
	font-size: 0.6875rem;
	line-height: 1.5;
	font-weight: 600;
`
const PageArrow = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border-radius: 50%;
	border: 1px solid #d3d3df;
	background: transparent;
	color: ${({ theme }) => theme.colors.independence};
	font-size: 1.5rem;
	cursor: pointer;
	&:hover:not(:disabled) {
		background: ${({ theme }) => theme.colors.primary};
	}
	&:disabled {
		opacity: 0.35;
		cursor: default;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.primary};
		outline-offset: 2px;
	}
`
